import { AxiosError } from 'axios';
import { BusinessPaymentMethod } from 'Hooks/Api/apiBusiness';
import { apiAddPaygCustomer, } from 'Hooks/Api/apiCustomer';
import { MonitorPaygPrice } from 'Hooks/Api/apiPrice';
import useOrganisationPlan from 'Hooks/Api/useOrganisationPlan';
import { useApiMonitorPayPrice } from 'Hooks/ApiHooks/useApiPrice';
import { debounce } from 'lodash';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { BusinessEntity, CustomerEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LocationUtil } from 'Util/LocationUtil';
import { gstRate } from 'Util/PaymentSummaryUtil';
import alertToast from 'Util/ToastifyUtils';
import { AbnInput } from 'Views/Components/BusinessNumberInputs/AbnInput';
import { AcnInput } from 'Views/Components/BusinessNumberInputs/AcnInput';
import { BusinessValidation } from 'Views/Components/BusinessNumberInputs/BusinessValidationModel';
import { BusinessPaymentSelector } from 'Views/Components/BusinessPaymentSelector/BusinessPaymentSelector';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import ButtonAsyncState from 'Views/Components/Button/ButtonAsyncState';
import CheckboxSetter from 'Views/Components/Checkbox/CheckboxSetter';
import { Combobox } from 'Views/Components/Combobox/Combobox';
import ComboInput from 'Views/Components/ComboInput/ComboInput';
import { OrgPlanErrorSection } from 'Views/Components/Error/OrgPlanErrorSection';
import If from 'Views/Components/If/If';
import { confirmModalAsync } from 'Views/Components/Modal/ModalUtils';
import { MonitorPaygSummaryCard } from 'Views/Components/PaymentSummaryCard/MonitorPaygSummaryCard';
import { PaymentSummaryCard } from 'Views/Components/PaymentSummaryCard/PaymentSummaryCard';
import InlineSpinner from 'Views/Components/Spinner/InlineSpinner';
import { IconDisplay, IconTextBox } from 'Views/Components/TextBox/IconTextBox';
import { useAddCustomerSearchParams } from './AddCustomer/addCustomerHooks';
import { CustomerStateLiquorLicenseDetails } from './CustomerDetailSections/CustomerStateLiquorLicenseDetails';
import LiquorLicenceDetailsTable from './LiquorLicenceSection/LiquorLicenceDetailsTable';

const confirmModalOptions = {
	shouldCloseOnOverlayClick: true,
};

function createAddCustomerRelationPath(model: CustomerEntity) {

	// Set relation path without group entity
	const relationPath = {
		groupss: {},
	};

	// Add groups to relationPath if we're adding new groups
	if (model.groupss.some(gc => gc.groups.id === undefined)) {
		relationPath.groupss = {
			groups: {},
		};
	}
	return relationPath;
}

function ConfirmAddCustomerPaygModalContent(props: {
	price: MonitorPaygPrice,
	abn: string,
	business: BusinessEntity
}) {

	const { business, abn, price } = props;

	return (
		<>
			<MonitorPaygSummaryCard summary={price} showDescription={true}>
				<CheckboxSetter
					label={`Monitor customer with ABN '${abn}' for ${price.billingPeriod} months at $${price.billingAmount}.`}
					value={true}
					isReadOnly={true}
				/>
			</MonitorPaygSummaryCard>
			<PaymentSummaryCard
				paymentSummary={{
					billedTo: business.name,
					orderItems: [{
						id: "monitoring",
						name: "Monitoring fee",
						price: price.billingAmount,
						originalPrice: price.billingAmount,
					}],
					gstRate: gstRate,
				}}
			/>
		</>
	)
}

function getOrganisationBusiness(businessId: string) {
	return store.getUser?.organisation?.businessEntitys.find(buseiness => buseiness.id === businessId);
}

const AddCustomer = observer(() => {

	const { abn, acn } = useAddCustomerSearchParams();

	const [showLiquorLicenceTable, setShowLiquorLicenceTable] = React.useState(false);
	const [paymentMethodId, setPaymentMethodId] = useState<string | undefined>(undefined);
	const [abnacnDetail] = useState<BusinessValidation>(new BusinessValidation({ abn, acn, requiredAbn: true }));
	const [trusteeAcnDetail] = useState<BusinessValidation>(new BusinessValidation());
	const [customer] = useState<CustomerEntity>(new CustomerEntity({
		organisationId: store.getUser?.organisation?.id,
	}));

	const { data: orgPlan, type: reqOrgPlanStatus } = useOrganisationPlan();
	const { data: priceModel, type: reqPriceModelStatus } = useApiMonitorPayPrice();

	const showLiquorLicenceSection = useMemo(() => {
		if (!customer.businessEntityId) return false;
		const businessEntityDetail = getOrganisationBusiness(customer.businessEntityId);
		if (!businessEntityDetail) return false;
		return businessEntityDetail.enableDrinksLicence;
	}, [customer.businessEntityId]);

	const validateLiquorLicence = useCallback(async () => await customer.validateField('drinksLicence'), [customer]);

	const debouncedValidateLiquorLicence = useCallback(debounce(validateLiquorLicence, 300), [validateLiquorLicence]);

	useEffect(() => {
		async function prefillCustomerDetails() {
			runInAction(async () => {
				// Pull the most appropriate business name: acn, trustee, then abn.
				customer.businessName = abnacnDetail?.acnExtraDetails?.name || trusteeAcnDetail?.acnExtraDetails?.name || abnacnDetail?.abnExtraDetails?.name || '';

				// Set sole trader information (if its a sole trader)
				if (abnacnDetail.abnExtraDetails?.typeCode === 'IND') {
					//@ts-ignore
					[customer.firstName, customer.lastName] = [abnacnDetail.abnExtraDetails.firstName, abnacnDetail.abnExtraDetails.lastName];
				}
			});
		}

		// Validation on customer entity
		async function validateOnCustomerFieldChange() {
			await customer.validateAddCustomerFields();
		}

		prefillCustomerDetails();
		validateOnCustomerFieldChange();
	}, [customer.abn, customer.acn, customer.trusteeACN, customer.businessEntityId, abnacnDetail.abn, abnacnDetail.acn, trusteeAcnDetail.acn])

	const onSetBusiness = (value: BusinessPaymentMethod) => {
		runInAction(() => {
			customer.organisationId = value.organisationId;
			customer.businessEntityId = value.businessId
		});
		setPaymentMethodId(value.paymentMethodId ?? undefined);
	}

	if (reqOrgPlanStatus === 'loading' || reqPriceModelStatus === 'loading') return <InlineSpinner />;
	if (reqOrgPlanStatus === 'error' || reqPriceModelStatus === 'error') return <OrgPlanErrorSection />;

	const editDisabled = store.userPermissions.intelManageCustomerGroups !== true;

	const addDisabled = () => {
		const someRequiredFieldsEmpty = customer.businessEntityId == null || !customer.abn;
		const isBussNumberInputValidating = !abnacnDetail.abnFinishedValidating || !abnacnDetail.acnFinishedValidating;
		const newCustomerIsInValid = Object.keys(customer.errors).length > 0;
		const businessNumberIsInvalid = (Object.keys(abnacnDetail.errors).length > 0) || (Object.keys(trusteeAcnDetail.errors).length > 0);
		return someRequiredFieldsEmpty || newCustomerIsInValid || businessNumberIsInvalid || invalidPayment() || isBussNumberInputValidating;
	}

	const handleOnAdd = async () => {
		if (orgPlan.accountType === 'PAYG') {
			await savePaygCustomer();
		} else if (orgPlan.accountType === 'INVOICED') {
			await saveInvoiceCustomer();
		} else {
			console.error(`Unable to save customer due to unsupported organisation account type: '${orgPlan.accountType}'`);
		}
	};

	const savePaygCustomer = async () => {
		const business = getOrganisationBusiness(customer.businessEntityId);
		if (business == null) {
			alertToast('Unable to find the users business', 'error');
			return;
		}
		const modal = confirmModalAsync("Confirm payment", <ConfirmAddCustomerPaygModalContent price={priceModel} abn={customer.abn} business={business} />, confirmModalOptions);
		try {
			await modal.result();
		} catch (err) {
			modal.close();
			return;
		}

		// Tech debt: This should be handled on the backend
		// But, migrating the GQL request to rest API for adding a customer has proven more work then expected.
		try {
			// 1) save customer without monitored status
			customer.monitoredStatus = 'NOT_MONITORED';
			const relationPath = createAddCustomerRelationPath(customer);
			await customer.save(relationPath);

			// 2) charge them
			const _ = await apiAddPaygCustomer({
				customerId: customer.id,
				businessId: customer.businessEntityId,
				monitorPricePaygAmountId: priceModel.id,
			});

			store.routerHistory.push(`/monitor/customers/${customer.id}`, {
				navigateTo: "business-information"
			});
		} catch (err) {
			alertToast((err as AxiosError).message, 'error');
		}

		modal.close();
	}
	async function saveInvoiceCustomer() {
		const relationPath = createAddCustomerRelationPath(customer);
		try {
			await customer.save(relationPath);
			store.routerHistory.push(`/monitor/customers/${customer.id}`, {
				navigateTo: "business-information"
			});
		} catch (err) {
			alertToast(`Error saving customer`, 'error');
		}
	}
	const invalidPayment = () => {
		if (orgPlan.accountType === 'INVOICED') return false;
		// PAYG organisation which requires payment
		if (paymentMethodId == null) return true;
		return false;
	}

	const updateTrusteeAcnError = () => {
		// Set error for matching acn/trustee acn if it exists
		if (customer.trusteeACN?.length === 9 && customer.acn === customer.trusteeACN) {
			trusteeAcnDetail.setFieldError('acn', true, 'ACN and Trustee ACN can not match');
			// Clear the specific error if its valid (we don't want to override the predefined acn errors)
		} else if (trusteeAcnDetail.errors['acn'] === 'ACN and Trustee ACN can not match') {
			delete trusteeAcnDetail.errors['acn'];
		}
	};

	const onAfterAbnAcnChange = (newValue: BusinessValidation) => {
		runInAction(() => {
			//@ts-ignore
			[customer.abn, customer.acn] = [newValue.abn, newValue.acn]
			updateTrusteeAcnError();
		})
	}

	const onAfterTrusteeAcnChange = (newValue: BusinessValidation) => {

		runInAction(() => {
			// @ts-ignore
			customer.trusteeACN = newValue.acn
			updateTrusteeAcnError();
		})
	}

	return (
		<div className="add-customer">
			<p className="section-title">Business information</p>
			<AbnInput
				model={abnacnDetail}
				isRequired
				onAfterChange={onAfterAbnAcnChange} />
			<AcnInput
				model={abnacnDetail}
				onAfterChange={onAfterAbnAcnChange} />
			<AcnInput
				model={trusteeAcnDetail}
				label='Trustee Australian Company Number (ACN)'
				onAfterChange={onAfterTrusteeAcnChange} />

			<BusinessPaymentSelector
				filterForEnabledMonitor={true}
				filterByOrgId={store.getUser?.organisation?.id}
				type={orgPlan.accountType}
				value={customer.businessEntityId}
				setValue={onSetBusiness}
			/>
			{showLiquorLicenceSection && (
				<>
					<p className="section-title">Liquor Licence information</p>
					<ComboInput
						label="Liquor Licence"
						errors={[customer.errors.drinksLicenceState, customer.errors.drinksLicence]}
					>
						<Combobox
							className="liquor-licence-state"
							model={customer}
							modelProperty="drinksLicenceState"
							label=""
							placeholder="State"
							options={LocationUtil.getStateOptions()}
							onAfterChange={debouncedValidateLiquorLicence}
							isRequired
							isDisabled={editDisabled}
							errors={customer.errors.drinksLicenceState}
							searchable={false}
						/>
						<IconTextBox
							model={customer}
							modelProperty="drinksLicence"
							placeholder="Licence Number"
							onChangeAndBlur={validateLiquorLicence}
							onAfterChange={debouncedValidateLiquorLicence}
							errors={customer.errors.drinksLicence}
							displayIconContainer={customer.liquorLicenceFinishedValidating}
							iconDisplay={customer.liquorLicenceResponseDetails?.responseCode === 0 ? IconDisplay.Valid : IconDisplay.Error}
							isRequired={false}
							isDisabled={editDisabled}
							label=""
						/>
					</ComboInput>
					<br />
					<small className="field-details">
						<If condition={Object.keys(customer.liquorLicenceResponseDetails?.data ?? {})?.length > 1}>
							<Button
								className="liquor-licence-details__button"
								display={Display.Text}
								colors={Colors.Primary}
								onClick={() => setShowLiquorLicenceTable(true)}
							>
								{`${customer.selectedLiquorLicenceDetails ? 'View' : 'Choose'} Licence Details`}
							</Button>
							<LiquorLicenceDetailsTable
								liquorLicenceDetails={customer.liquorLicenceResponseDetails}
								liquorLicenceNumber={customer.drinksLicence}
								liquorLicenceState={customer.drinksLicenceState}
								previousUniqueId={customer.selectedLiquorLicenceDetails?.uniqueId}
								updateLiquorLicenceDetails={customer.updateLiquorLicenceFields}
								openModal={showLiquorLicenceTable}
								closeModal={() => setShowLiquorLicenceTable(false)}
							/>
						</If>

						<CustomerStateLiquorLicenseDetails customer={customer} />
					</small>
				</>
			)}

			<div className="button-container">
				<Button
					colors={Colors.Primary}
					display={Display.Outline}
					onClick={() => store.routerHistory.push('/monitor/customers')}
				>
					Cancel
				</Button>
				<ButtonAsyncState
					colors={Colors.Primary}
					display={Display.Solid}
					readonly={addDisabled()}
					onPress={handleOnAdd}
				>
					Add
				</ButtonAsyncState>
			</div>
		</div>
	);
});
export default AddCustomer;
